import React, { useCallback, useEffect, useState } from 'react';
import { Button, CircularProgress, IconButton, TextField, Tooltip } from '@mui/material';
import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../hooks/useNotification';
import { createUserMemory, deleteUserMemory, listUserMemories } from '../../../services/api';
import type { UserMemory } from '../../../types';
import {
  PROJECT_MEMORY_CATEGORY,
  PROJECT_MEMORY_KEY_PREFIX,
  PROJECT_MEMORY_MAX_LENGTH,
} from '../constants';
import { ProjectEmptyState } from './ProjectEmptyState';

interface ProjectMemoryPanelProps {
  projectId: string;
}

/** Durable notes shared with every conversation in the project (injected into the prompt). */
export const ProjectMemoryPanel: React.FC<ProjectMemoryPanelProps> = ({ projectId }) => {
  const { t } = useTranslation();
  const { error: showError } = useNotification();
  const [memories, setMemories] = useState<UserMemory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    const response = await listUserMemories({ projectId });
    if (response.success && response.data) {
      setMemories(response.data);
    } else {
      showError(response.error ?? t('projects.memory.loadFailed'));
    }
    setIsLoading(false);
  }, [projectId, showError, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAdd = async () => {
    const value = draft.trim();
    if (!value || isAdding) return;

    setIsAdding(true);
    const response = await createUserMemory({
      projectId,
      category: PROJECT_MEMORY_CATEGORY,
      key: `${PROJECT_MEMORY_KEY_PREFIX}.${crypto.randomUUID().slice(0, 8)}`,
      value,
    });
    if (response.success && response.data) {
      setMemories((current) => [response.data!, ...current]);
      setDraft('');
    } else {
      showError(response.error ?? t('projects.memory.addFailed'));
    }
    setIsAdding(false);
  };

  const handleDelete = async (memory: UserMemory) => {
    const response = await deleteUserMemory(memory.publicId);
    if (response.success) {
      setMemories((current) => current.filter((item) => item.publicId !== memory.publicId));
    } else {
      showError(response.error ?? t('projects.memory.deleteFailed'));
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-gray-600 dark:text-slate-400">{t('projects.memory.hint')}</p>
      <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800 sm:flex-row sm:items-start">
        <TextField
          fullWidth
          multiline
          maxRows={5}
          size="small"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('projects.memory.placeholder')}
          inputProps={{ maxLength: PROJECT_MEMORY_MAX_LENGTH }}
        />
        <Button
          variant="contained"
          size="small"
          disabled={!draft.trim() || isAdding}
          onClick={() => void handleAdd()}
          className="shrink-0"
        >
          {t('projects.memory.add')}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <CircularProgress size={24} />
        </div>
      ) : memories.length === 0 ? (
        <ProjectEmptyState
          title={t('projects.memory.emptyTitle')}
          description={t('projects.memory.emptyDescription')}
        />
      ) : (
        <ul className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
          {memories.map((memory) => (
            <li
              key={memory.publicId}
              className="flex items-start gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0 dark:border-slate-700"
            >
              <p className="min-w-0 flex-1 whitespace-pre-line break-words text-sm text-gray-800 dark:text-slate-200">
                {memory.value}
              </p>
              <Tooltip title={t('projects.memory.delete')}>
                <IconButton
                  size="small"
                  aria-label={t('projects.memory.delete')}
                  onClick={() => void handleDelete(memory)}
                  className="text-gray-400 hover:text-red-600 dark:text-slate-500 dark:hover:text-red-400"
                >
                  <Trash2 size={16} />
                </IconButton>
              </Tooltip>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
